import { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import { getTraceCapabilities, unionCapabilities } from '../capabilities';
import { EditorCapabilities, deriveEditorCapabilities } from './editorCapabilities';
import { ChartCardV1 } from '../../../../../../packages/db-schema/shared/chartcards/types';

export type PlotUpdateHandler = (updateType: string, payload: any) => void;

export interface PlotEditorContextValue {
  plotlyJson: any;
  dataColumns?: string[];
  chartType: string;
  onUpdate: PlotUpdateHandler;
  // Selection state removed - use Zustand store directly via usePlotsStore()
  // Legacy fields kept for backward compatibility but deprecated
  selectedTraceIndex: number | null;
  setSelectedTraceIndex: (idx: number | null) => void;
  selectedAnnotationIndex: number | null;
  setSelectedAnnotationIndex: (idx: number | null) => void;
  selectedShapeIndex: number | null;
  setSelectedShapeIndex: (idx: number | null) => void;
  // Derived flags (kept for backward compatibility)
  hasBars: boolean;
  hasHistogram: boolean;
  hasPerPointColors: boolean;
  hasFacets: boolean;
  hasScatter: boolean;
  hasLine: boolean;
  // New capabilities model
  editorCapabilities: EditorCapabilities;
}

const PlotEditorContext = createContext<PlotEditorContextValue | null>(null);

export interface PlotEditorContextProviderProps {
  plotlyJson: any;
  dataColumns?: string[];
  chartType: string;
  chartCard?: ChartCardV1 | null;
  planSpec?: any;
  onUpdate: PlotUpdateHandler;
  children: ReactNode;
}

export function PlotEditorContextProvider({
  plotlyJson,
  dataColumns,
  chartType,
  chartCard,
  planSpec,
  onUpdate,
  children,
}: PlotEditorContextProviderProps) {
  // Selection state migrated to Zustand - keep local state as thin proxy for backward compatibility
  // These are no longer the source of truth; consumers should migrate to usePlotsStore()
  const [selectedTraceIndex, setSelectedTraceIndex] = useState<number | null>(null);
  const [selectedAnnotationIndex, setSelectedAnnotationIndex] = useState<number | null>(null);
  const [selectedShapeIndex, setSelectedShapeIndex] = useState<number | null>(null);

  // Compute derived flags (kept for backward compatibility)
  const derivedFlags = useMemo(() => {
    const traces = Array.isArray(plotlyJson?.data) ? plotlyJson.data : [];
    const caps = unionCapabilities(traces);
    
    return {
      hasBars: traces.some((t: any) => t.type === 'bar'),
      hasHistogram: traces.some((t: any) => t.type === 'histogram' || t.type === 'histogram2d'),
      hasPerPointColors: !!caps.hasPerPointColors,
      hasFacets: !!(plotlyJson?.layout?.xaxis2 || plotlyJson?.layout?.yaxis2),
      hasScatter: traces.some((t: any) => t.type === 'scatter' || t.type === 'scattergl'),
      hasLine: traces.some((t: any) => (t.type === 'scatter' || !t.type) && t.mode?.includes('lines')),
    };
  }, [plotlyJson?.data]);

  // Derive editor capabilities
  const editorCapabilities = useMemo(() => {
    return deriveEditorCapabilities({
      chartCard: chartCard || null,
      planSpec: planSpec || null,
      plotlyJson: plotlyJson || {},
      dataColumns: dataColumns || [],
    });
  }, [chartCard, planSpec, plotlyJson, dataColumns]);

  const value: PlotEditorContextValue = {
    plotlyJson,
    dataColumns,
    chartType,
    onUpdate,
    selectedTraceIndex,
    setSelectedTraceIndex,
    selectedAnnotationIndex,
    setSelectedAnnotationIndex,
    selectedShapeIndex,
    setSelectedShapeIndex,
    ...derivedFlags,
    editorCapabilities,
  };

  return (
    <PlotEditorContext.Provider value={value}>
      {children}
    </PlotEditorContext.Provider>
  );
}

export function usePlotEditorContext(): PlotEditorContextValue {
  const context = useContext(PlotEditorContext);
  if (!context) {
    throw new Error('usePlotEditorContext must be used within PlotEditorContextProvider');
  }
  return context;
}

/**
 * Convenience hook to access editor capabilities
 */
export function useCaps(): EditorCapabilities {
  const ctx = usePlotEditorContext();
  return ctx.editorCapabilities;
}

